import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../../renderer/TextsProvider.js';
import { UpsellModal } from './upsellModal.js';
import { acceptPostPurchaseOffer, fetchPostPurchaseChain, settlePostPurchaseOffer, } from './postPurchaseUpsell.js';
import { UpsellOfferCard } from './UpsellOfferCard.js';
/**
 * The post-purchase 1-Click Upsell chain, played on the order-confirmation page (feature 0042 lot E).
 *
 * The offer plays in a POPUP over the confirmation page (founder, 2026-08-10), not as a block inside it.
 * The page underneath keeps saying "your order is confirmed" the whole time, and the overlay closes on
 * Escape, on the backdrop and on its own × — a buyer whose order is already placed owes this nothing and
 * must never feel trapped on their own confirmation page. A COD shopper who closes the tab has still been
 * confirmed, and the order id lives in the URL, so a refresh re-serves the page.
 *
 * **The server owns the money.** Accepting POSTs `(offer, variant, quantity)` and the response carries the
 * order as the server now holds it; [onOrderUpdated] hands that straight to the page. Nothing here adds a
 * line to a total. That is what makes the number on this page and the number the courier collects the same
 * number, which in Cash-on-Delivery is the entire transaction.
 *
 * Every failure resolves quietly: no chain, an unreachable backend, an offer that went stale — the chain just
 * ends, and the confirmation the buyer already has stands untouched.
 */
export function PostPurchaseUpsellChain({ orderId, store, onOrderUpdated, fetchImpl, presentation = 'modal', onFinished, onHasContent, authenticate, }) {
    const [chain, setChain] = useState(null);
    const [index, setIndex] = useState(0);
    const [busy, setBusy] = useState(false);
    const t = useT();
    // The note holds a KEY, not a sentence (feature 0050): translating at render keeps `accept` free of a
    // dependency on the translator, and a sentence frozen into state could not follow the shop's language.
    const [note, setNote] = useState(null);
    const [deadlines, setDeadlines] = useState({});
    /** Once the buyer has ended the chain (or it ran out), it never comes back on this page view. */
    const [finished, setFinished] = useState(false);
    // The chain is fetched ONCE per page view. A confirmation page is not polled: the offer a buyer sees is the
    // one that was live when they landed, and re-fetching would change it under their finger.
    // Fires `onFinished` once and only once, whichever way the chain ended.
    const finishedOnce = useRef(false);
    const finish = useCallback(() => {
        setFinished(true);
        if (finishedOnce.current)
            return;
        finishedOnce.current = true;
        onFinished?.();
    }, [onFinished]);
    const loaded = useRef(false);
    useEffect(() => {
        if (loaded.current || !orderId)
            return;
        loaded.current = true;
        let alive = true;
        void fetchPostPurchaseChain(orderId, { store, fetchImpl }).then((result) => {
            if (!alive)
                return;
            // No chain, an empty one, or an unreachable backend: there is nothing to show and nothing to wait
            // for. Say so immediately — a host holding a navigation on this must not hang on silence.
            if (!result || result.offers.length === 0) {
                finish();
                return;
            }
            setChain(result);
            // Every countdown starts when the chain appears, not when its offer does: a timer that restarted on
            // each card would promise the same "last minutes" three times over.
            const now = Date.now();
            const starts = {};
            for (const offer of result.offers) {
                if (offer.design.timer.enabled) {
                    starts[offer.offerId] = now + Math.max(0, offer.design.timer.minutes) * 60_000;
                }
            }
            setDeadlines(starts);
        });
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `finish` is stable enough; the ref gates re-runs
    }, [orderId, store, fetchImpl]);
    const advance = useCallback(() => {
        setIndex((i) => {
            const next = i + 1;
            if (!chain || next >= chain.offers.length)
                finish();
            return next;
        });
    }, [chain, finish]);
    const accept = useCallback((line) => {
        if (!chain || busy)
            return;
        setBusy(true);
        setNote(null);
        void acceptPostPurchaseOffer(orderId, {
            upsellId: chain.upsellId,
            version: chain.version,
            offerId: line.offerId,
            variantId: line.variantId,
            quantity: line.quantity,
        }, { store, fetchImpl }).then(async (result) => {
            if (!result) {
                setBusy(false);
                // The call did not complete, so nothing was written. Say so plainly and let them carry on — the
                // order they are looking at is still exactly right.
                setNote('cod.upsell_failed');
                advance();
                return;
            }
            // The bank wants the buyer to authenticate (feature 0069 lot E). Nothing has been written and the
            // card has not been charged: this is a STEP, not a failure, and the buyer is right here to take it.
            // Stays `busy` throughout — the card must not accept a second tap while their bank's sheet is up.
            if (result.clientSecret && result.stripeAccountId) {
                const answered = authenticate === undefined
                    ? false
                    : await authenticate({
                        clientSecret: result.clientSecret,
                        stripeAccountId: result.stripeAccountId,
                    }).catch(() => false);
                // The server is asked whether the money landed — never the browser's word for it. A buyer who
                // closes the sheet, a bank that refuses, a host with no authenticator: all end here, unwritten.
                const settled = answered
                    ? await settlePostPurchaseOffer(orderId, {
                        upsellId: chain.upsellId,
                        version: chain.version,
                        offerId: line.offerId,
                        variantId: line.variantId,
                        quantity: line.quantity,
                    }, { store, fetchImpl })
                    : null;
                setBusy(false);
                if (!settled || !settled.order) {
                    // They abandoned the challenge, their bank refused, or the answer never came back. The money did
                    // not move — the server only writes the line once Stripe says it has.
                    setNote('cod.upsell_failed');
                    advance();
                    return;
                }
                if (!settled.accepted) {
                    setNote('cod.upsell_gone');
                    advance();
                    return;
                }
                onOrderUpdated(settled.order);
                setNote('cod.upsell_added');
                advance();
                return;
            }
            setBusy(false);
            if (!result.accepted || !result.order) {
                setNote('cod.upsell_gone');
                advance();
                return;
            }
            onOrderUpdated(result.order);
            setNote('cod.upsell_added');
            advance();
        });
    }, [chain, busy, orderId, store, fetchImpl, advance, onOrderUpdated, authenticate]);
    const offer = chain && !finished ? chain.offers[index] : undefined;
    // The host owns the shell, so it must know the instant there is something to show — and the instant there
    // is no longer anything. Announced from an EFFECT, never during render, so the parent's setState is legal.
    // It sits above the early return below because a hook may not live behind a condition.
    const hasContent = !!offer || !!note;
    useEffect(() => {
        onHasContent?.(hasContent);
    }, [hasContent, onHasContent]);
    // Nothing on screen — while the chain is still being fetched, and for good when there is none. It renders
    // NOTHING in either presentation: the host keeps what the buyer was already looking at (their confirmed
    // order) instead of being handed an empty shell. A skeleton used to fill the INLINE case here, but it only
    // existed because the host gave its shell away the moment the order was placed — so every order with no
    // offer to play flashed an empty popup and then redirected (founder, 23/09/2026).
    if (!hasContent)
        return null;
    // Keyed by offer: each offer gets a NEW card. The card owns its own busy flag and double-tap latch, and
    // without the key React kept offer 1's card for offer 2 — which then opened already saying « Ajout en
    // cours… », its « Non merci » greyed out, for as long as the buyer stared at it (founder, 23/09/2026).
    // The pre-purchase chains (cart and buy-box) were keyed from day one; this one was not.
    const card = offer ? (_jsx(UpsellOfferCard, { offer: offer, index: index + 1, total: chain?.offers.length ?? 1, deadline: deadlines[offer.offerId] ?? null, onAccept: accept, onReject: advance, onSkipChain: finish }, offer.offerId)) : null;
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: PPU_CSS }), note ? (_jsx("section", { className: "ppu", "aria-live": "polite", children: _jsx("p", { className: "ppu-note", role: "status", children: t(note) }) })) : null, card && presentation === 'inline' ? (_jsxs("div", { className: busy ? 'ppu-inline is-busy' : 'ppu-inline', children: [_jsx("p", { className: "ppu-placed", children: t('cod.upsell_order_placed') }), card] })) : card ? (_jsxs(UpsellModal, { busy: busy, onClose: finish, children: [_jsx("p", { className: "ppu-placed", children: t('cod.upsell_order_placed') }), card] })) : null] }));
}
/**
 * Static only, and it must stay that way: not one seller-supplied value belongs in a stylesheet. The offer's
 * own look comes from the card, applied through React `style` objects.
 */
const PPU_CSS = `
.ppu { display:grid; gap:12px; margin: 0 0 18px; }
.ppu-note {
  margin:0; padding:11px 14px; border-radius:12px; font-size:14px; line-height:1.45;
  background: rgba(16,185,129,.10); color:#065f46; border:1px solid rgba(16,185,129,.28);
}
/* Busy = the offer is being added; the INLINE variant only (the modal's twin rule travels with the modal).
   The card stays FULLY VISIBLE and only stops accepting clicks: fading it to 60% made the screen look
   frozen, and a buyer who thinks a screen is frozen taps again or closes the tab — on an order that is
   going through (founder, 2026-08-12). The button says what is happening. */
.ppu-inline.is-busy { pointer-events:none; }
/* « Your order is placed » — the ONE line that tells a pre-purchase offer from a post-purchase one.
   Without it the two windows are identical: same frame, same seller copy, and on the COD funnel the page
   behind still shows the order button. The founder read a post-purchase offer as a pre-sell on her own
   shop (23/09/2026) — a buyer must know whether she is deciding BEFORE ordering or adding to an order that
   already exists. Quiet on purpose: it informs, it does not compete with the offer. */
.ppu-placed {
  margin:0 0 14px; padding:9px 12px; border-radius:10px; font-size:13px; line-height:1.45;
  background:rgba(16,185,129,.10); color:#065f46; border:1px solid rgba(16,185,129,.24);
}
@media (prefers-color-scheme: dark) {
  .ppu-placed { background:rgba(16,185,129,.14); color:#6ee7b7; border-color:rgba(16,185,129,.34); }
}
.ppu-load { display:flex; flex-direction:column; gap:12px; padding:8px 0; }
.ppu-load span {
  display:block; block-size:14px; border-radius:7px; background:rgba(0,0,0,.07);
  animation:ppuPulse 1.1s ease-in-out infinite;
}
.ppu-load span:nth-child(2) { block-size:150px; }
.ppu-load span:nth-child(3) { inline-size:60%; align-self:center; }
@keyframes ppuPulse { 0%,100% { opacity:.55 } 50% { opacity:1 } }
@media (prefers-color-scheme: dark) {
  .ppu-note { background: rgba(16,185,129,.14); color:#6ee7b7; border-color: rgba(16,185,129,.34); }
  .ppu-load span { background:rgba(255,255,255,.08); }
}
@media (prefers-reduced-motion:reduce) { .ppu-load span { animation:none } }
`;
//# sourceMappingURL=PostPurchaseUpsellChain.js.map